import { useRef, useState } from "preact/hooks";

import type { AssigneeOption } from "./AssigneePickerSheet";
import { AssigneePickerSheet } from "./AssigneePickerSheet";
import { useModalA11y } from "../hooks/useModalA11y";
import type { CalendarMutationResult } from "../hooks/useSchedule";
import {
  CALENDAR_GENERIC_ERROR,
  CREATE_SHEET_SUBMIT_LABEL,
  CREATE_SHEET_WHAT_LABEL,
  CREATE_SHEET_WHO_LABEL,
  createSheetTitle,
} from "../lib/calendarCopy";
import { UP_FOR_GRABS_LABEL } from "../lib/copy";

export interface CalendarCreateSheetProps {
  open: boolean;
  dateLabel: string;
  /** Sprint-010 C4 (design-phase-c.md §5.4) — Quick Add's "Add a task"
   * flow reuses this sheet's exact two fields, but with its own
   * deliberately-shorter title ("Add a task for today," dropping
   * `createSheetTitle`'s "one-time" — m-C0b-8, confirmed scoped
   * exception, not an unflagged drift). When given, this REPLACES
   * `createSheetTitle(dateLabel)` entirely. */
  titleOverride?: string;
  roster: AssigneeOption[];
  onAdd: (input: { title: string; assigneeId?: string }) => Promise<CalendarMutationResult>;
  onCancel: () => void;
}

/**
 * §2.7 — the create-one-off bottom sheet, admin/manager/api only (a member
 * never sees a "+" affordance on the calendar at all — see
 * `views/CalendarScreen.tsx`'s `canWrite` gate on every trigger). Reuses
 * `AddHealthNoteSheet`'s exact chrome (backdrop, Escape, focus trap via
 * `useModalA11y`, synchronous-reset-on-open discipline) and
 * `AssigneePickerSheet` verbatim for "Who does it."
 *
 * **Flagged deviation** (see PR body): §2.7's mock also has a "Where
 * (optional)" field, but neither `AssignmentCreateIn`
 * (`app/schemas/assignments.py`) nor `ScheduleOccurrenceOut`
 * (`app/schemas/schedule.py`) carries an `area` field for an ad-hoc
 * one-off (only cadence-backed rows have `area`) — there is nowhere for
 * that value to be stored OR ever displayed once saved. Rather than
 * silently write it into the unrelated `notes` field (where it would
 * never surface anywhere in the UI), this field is omitted from this
 * build. Flagged for Solution Architect: either add `area` to
 * `assignments`/`AssignmentCreateIn`/`ScheduleOccurrenceOut`, or confirm
 * the field should be cut from the spec.
 */
export function CalendarCreateSheet({
  open,
  dateLabel,
  titleOverride,
  roster,
  onAdd,
  onCancel,
}: CalendarCreateSheetProps) {
  const [title, setTitle] = useState("");
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setTitle("");
    setAssigneeId(null);
    setPickerOpen(false);
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly. `active` drops to false
  // while the nested `AssigneePickerSheet` is open (see below), so this
  // entry's ephemeral history push is reconciled away during that window
  // and re-pushed when the picker closes and this sheet re-activates —
  // the picker becomes the sole topmost entry for the duration.
  const { close } = useModalA11y(containerRef, {
    active: open && !pickerOpen,
    onClose: onCancel,
    initialFocusRef: titleRef,
  });

  if (!open) return null;

  const assigneeName = assigneeId
    ? (roster.find((r) => r.id === assigneeId)?.name ?? UP_FOR_GRABS_LABEL)
    : UP_FOR_GRABS_LABEL;

  async function submit() {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    const result = await onAdd({ title: title.trim(), assigneeId: assigneeId ?? undefined });
    setSaving(false);
    if (result.ok) {
      onCancel();
      return;
    }
    setError(CALENDAR_GENERIC_ERROR);
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-create-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="calendar-create-title">
          {titleOverride ?? createSheetTitle(dateLabel)}
        </h2>

        <label class="admin-field-label" for="calendar-create-title-input">
          {CREATE_SHEET_WHAT_LABEL}
        </label>
        <input
          ref={titleRef}
          id="calendar-create-title-input"
          type="text"
          class="pick-input"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
        />

        <span class="admin-field-label">{CREATE_SHEET_WHO_LABEL}</span>
        <button
          type="button"
          class="pick-input calendar-create-assignee-btn"
          onClick={() => setPickerOpen(true)}
        >
          {assigneeName} ▾
        </button>

        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!title.trim() || saving}
          onClick={() => void submit()}
        >
          {saving ? "Adding…" : CREATE_SHEET_SUBMIT_LABEL}
        </button>
        <button type="button" class="secondary-btn" style={{ marginTop: "10px" }} onClick={close}>
          Cancel
        </button>
      </div>

      <AssigneePickerSheet
        open={pickerOpen}
        pieceTitle={title || "this task"}
        options={roster}
        selectedId={assigneeId}
        onSelect={(id) => {
          setAssigneeId(id);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />
    </>
  );
}
